import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const HIDE_AFTER_SCROLL_PX = 80; // 이 거리 이상 스크롤되어야 hide/show 트리거
// 토글 페이지 간 전환을 감지하기 위한 임계값. 이 시간 안에 다른 토글 경로에서 재마운트되면
// "토글 페이지 간 이동" 으로 간주하고 인트로 애니메이션을 건너뛴다.
const INTRA_TOGGLE_NAV_MS = 1500;

// 마지막으로 토글 컴포넌트가 언마운트된 경로와 시각 (모듈 레벨로 컴포넌트 인스턴스를 가로질러 유지).
let lastTogglePath: string | null = null;
let lastTogglePathTime = 0;
// 탭(Link) 클릭으로 페이지 전환이 발생한 경우 true — 다음 마운트에서 인트로 슬라이드 인을 건너뛴다.
// (페이지 컴포넌트가 unmount/mount 되는 사이에도 잃지 않도록 모듈 레벨로 둠.)
let suppressNextIntro = false;

type ToggleKey = 'life' | 'todo' | 'daily' | 'study' | 'hideout';

const resolveKey = (pathname: string): ToggleKey => {
  if (pathname.startsWith('/todo')) return 'todo';
  if (pathname.startsWith('/daily')) return 'daily';
  if (pathname.startsWith('/study')) return 'study';
  if (pathname.startsWith('/hideout')) return 'hideout';
  return 'life';
};

// Life 그룹(life, hideout)은 다른 탭 셋(Life / Hideout)을 사용한다.
// 그 외(todo, daily, study)는 기존 4탭(Life / Todo / Daily / Study) 그대로.
const isLifeGroup = (key: ToggleKey) => key === 'life' || key === 'hideout';

const LifeTodoToggle: React.FC = () => {
  const location = useLocation();
  const active = resolveKey(location.pathname);

  // 인트로 재생 여부 결정: 첫 진입이거나(아직 lastTogglePath 없음),
  // 마지막 토글 경로와 다르고 1.5초 이상 경과했을 때 (= 다른 페이지를 다녀왔거나 새로고침) 재생.
  // 단, 직전에 토글 탭(Link)을 클릭해 이동한 경우는 무조건 건너뜀.
  const [shouldPlayIntro] = useState<boolean>(() => {
    if (suppressNextIntro) {
      suppressNextIntro = false;
      return false;
    }
    if (lastTogglePath === null) return true;
    const elapsed = Date.now() - lastTogglePathTime;
    const isIntraNav = lastTogglePath !== location.pathname && elapsed < INTRA_TOGGLE_NAV_MS;
    return !isIntraNav;
  });

  const handleTabClick = () => {
    suppressNextIntro = true;
  };

  // 첫 진입은 화면 밖(아래)에서 슬라이드 업, 페이지 이동 시에는 이미 보이는 상태로 시작.
  const [hidden, setHidden] = useState(shouldPlayIntro);
  // 인트로 슬라이드 인 동안만 bounce easing 적용 (이후 스크롤 hide/show는 일반 ease)
  const [isIntroAnimating, setIsIntroAnimating] = useState(shouldPlayIntro);
  const lastScrollYRef = useRef<number>(typeof window !== 'undefined' ? window.scrollY : 0);

  useEffect(() => {
    if (!shouldPlayIntro) return;
    // 페인트 후 hidden 해제 → CSS transition으로 슬라이드 인
    const revealTimer = setTimeout(() => setHidden(false), 50);
    // 인트로 애니메이션 끝난 뒤 일반 easing으로 전환
    const settleTimer = setTimeout(() => setIsIntroAnimating(false), 1100);
    return () => {
      clearTimeout(revealTimer);
      clearTimeout(settleTimer);
    };
  }, [shouldPlayIntro]);

  // 언마운트 시점 기록 — 다음 마운트에서 인트로 여부 판단에 사용
  useEffect(() => {
    return () => {
      lastTogglePath = location.pathname;
      lastTogglePathTime = Date.now();
    };
  }, [location.pathname]);

  const introTransition = 'transform 0.9s cubic-bezier(0.34, 1.56, 0.64, 1)';
  const normalTransition = 'transform 0.8s ease';
  const activeTransition = isIntroAnimating ? introTransition : normalTransition;

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      const prev = lastScrollYRef.current;
      if (y > prev && y > HIDE_AFTER_SCROLL_PX) {
        setHidden(true);
      } else if (y < prev) {
        setHidden(false);
      }
      lastScrollYRef.current = y;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <div
        className='lifeTodoToggleBackdrop'
        aria-hidden
        style={{
          transform: hidden ? 'translateY(100%)' : 'translateY(0)',
          transition: activeTransition,
        }}
      />
    <div
      className='lifeTodoToggle'
      role='tablist'
      aria-label='Life / Todo 전환'
      style={{
        transform: hidden
          ? 'translate(-50%, calc(100% + 48px))'
          : 'translate(-50%, 0)',
        transition: activeTransition,
      }}
    >
      {isLifeGroup(active) ? (
        <>
          <Link
            to='/life'
            className={`lifeTodoToggleItem ${active === 'life' ? 'active' : ''}`}
            role='tab'
            aria-selected={active === 'life'}
            onClick={handleTabClick}
          >
            Life
          </Link>
          <Link
            to='/hideout'
            className={`lifeTodoToggleItem ${active === 'hideout' ? 'active' : ''}`}
            role='tab'
            aria-selected={active === 'hideout'}
            onClick={handleTabClick}
          >
            Hideout
          </Link>
        </>
      ) : (
        <>
          <Link
            to='/life'
            className={`lifeTodoToggleItem ${active === 'life' ? 'active' : ''}`}
            role='tab'
            aria-selected={active === 'life'}
            onClick={handleTabClick}
          >
            Life
          </Link>
          <Link
            to='/todo'
            className={`lifeTodoToggleItem ${active === 'todo' ? 'active' : ''}`}
            role='tab'
            aria-selected={active === 'todo'}
            onClick={handleTabClick}
          >
            Todo
          </Link>
          <Link
            to='/daily'
            className={`lifeTodoToggleItem lifeTodoToggleItem--daily ${active === 'daily' ? 'active' : ''}`}
            role='tab'
            aria-selected={active === 'daily'}
            onClick={handleTabClick}
          >
            Daily
          </Link>
          <Link
            to='/study'
            className={`lifeTodoToggleItem ${active === 'study' ? 'active' : ''}`}
            role='tab'
            aria-selected={active === 'study'}
            onClick={handleTabClick}
          >
            Study
          </Link>
        </>
      )}
    </div>
    </>
  );
};

export default LifeTodoToggle;
